import { TransferMethod } from '@/types/schema';

import { DispositiveProvisionsFormRecipient } from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';

export const DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE =
  'dispositiveProvisionsV2';

/**
 * The form will have at most two disposition scenarios: one for each grantor's
 * death event, given that the "selectedFirstPrimaryClientDeathId" dies first.
 */
export interface DispositiveProvisionsFormV2Shape {
  [DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE]: DispositiveProvisionsFormV2_DispositionScenario;
}

export interface DispositiveProvisionsFormV2_DispositionScenario {
  /**
   * The ID of the primary client that is selected to die first.
   */
  selectedFirstPrimaryClientDeathId: string;
  /**
   * The disposition scenario ID for an Entity/CP/TE's DP's.
   * This will be null for new scenarios that have not been saved yet.
   */
  dispositionScenarioId: string | null;
  /**
   * The ID of the associated waterfall for this disposition scenario.
   * This will be null for new scenarios that have not been saved yet.
   */
  _dispositionScenarioAssociatedWaterfallId: string | null;
  firstDeath: DispositiveProvisionsFormV2_DeathEventProvisions;
  secondDeath: DispositiveProvisionsFormV2_DeathEventProvisions | null;
  /**
   * The transfer method for this disposition scenario.
   * Defaults to "IN_KIND" if not specified.
   */
  transferMethod: TransferMethod;
}

/**
 * The DispositiveProvisionsFormV2_DeathEventProvisions represents recipients
 * for a grantor's death event of a disposition scenario, where the
 * "selectedFirstPrimaryClientDeathId" dies first.
 */
export interface DispositiveProvisionsFormV2_DeathEventProvisions {
  /**
   * The death event for these provisions.
   */
  deathEvent: PrimaryClientDeathEvent;
  recipients: DispositiveProvisionsFormRecipient[];
  templateId?: string | null;
  /**
   * Whether all taxes are paid out of residuary for this death event.
   * Defaults to true if it is not specified.
   */
  residuaryPaysAllTaxes: boolean;
}

export enum PrimaryClientDeathEvent {
  FIRST_DEATH = 'FIRST_DEATH',
  SECOND_DEATH = 'SECOND_DEATH',
}

export enum DeathSectionDistributionStatus {
  /**
   * All assets have been accounted for – either because the recipients
   * explicitly include an "Anything Left Over" disposition, or because the
   * sum of recipient disposition percentages is exactly 100%.
   */
  FULLY_DISTRIBUTED = 'FULLY_DISTRIBUTED',

  /**
   * Assets still remain to be distributed, but they can be distributed by
   * referencing the other death event's provisions (i.e. the *other* death
   * event is fully distributed).  This also represents the state where there
   * are zero recipients for both death events.
   */
  ASSETS_REMAIN = 'ASSETS_REMAIN',

  /**
   * The current death event is missing a residuary clause – recipients exist
   * but they do not fully distribute the assets and the complementary death
   * event does not cover the remainder.
   */
  NEEDS_RESIDUARY = 'NEEDS_RESIDUARY',

  /**
   * The recipients' percentage distributions sum to more than 100%, which
   * represents an invalid distribution configuration that needs correction.
   */
  EXCEEDS_100_PERCENT = 'EXCEEDS_100_PERCENT',

  /**
   * No assets remain to be distributed because the first death event has
   * already fully distributed all assets. This status is only applicable
   * to the second death event.
   */
  NO_ASSETS_REMAIN = 'NO_ASSETS_REMAIN',
}
